import { BonusClassification, Currency, getBonusClassificationTranslationId } from '@synergy/models';
import { Box, Stack, Tooltip } from '@mui/material';
import { CommentNoteIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    OverviewTableComponent,
    OverviewTableComponentProps,
    PoolTableColumn,
    PoolTableRow,
    ValueOrNDash,
} from '@synergy/components';
import { PoolFormattedMessage, PoolTypography } from '@synergy/components-next';

import { BonusConditionMode, CustomerBonusSettlementSupplier } from '../../models/customer-bonus-settlement-supplier';
import { BonusSettlementDetailBonusConditionTooltip } from './bonus-settlement-detail-bonusCondition-tooltip';

export interface BonusSettlementSupplierOverviewTableProps
    extends OverviewTableComponentProps<CustomerBonusSettlementSupplier> {
    currency: Currency;
}

export class BonusSettlementDetailTable extends OverviewTableComponent<
    CustomerBonusSettlementSupplier,
    BonusSettlementSupplierOverviewTableProps
> {
    protected columns: PoolTableColumn[] = [
        {
            id: 'supplier',
            label: <FormattedMessage id="app.bonusSettlements.detail.table.supplier" />,
            sortable: false,
            width: '30%',
        },
        {
            id: 'bonusClassification',
            label: <FormattedMessage id="app.bonusSettlements.detail.table.bonusClassification" />,
            sortable: false,
        },
        {
            id: 'information',
            sortable: false,
            width: '1%',
        },
        {
            id: 'modePercentage',
            label: <PoolFormattedMessage id="app.bonusSettlements.detail.mode.percentage" />,
            sortable: false,
            textAlign: 'right',
            width: '10%',
        },
        {
            id: 'modeAmount',
            label: <PoolFormattedMessage id="app.bonusSettlements.detail.mode.amount" />,
            sortable: false,
            textAlign: 'right',
            width: '10%',
        },
        {
            id: 'memberBonus',
            label: <PoolFormattedMessage id="app.bonusSettlements.detail.memberBonus" />,
            sortable: false,
            textAlign: 'right',
            width: '10%',
        },
    ];

    protected getRows = () =>
        this.props.data.items.flatMap<PoolTableRow>(bonusSettlementSupplier => {
            // BONUS_PLUS is displayed as CONTRACT to the customer
            const bonusClassification =
                bonusSettlementSupplier.bonusClassification === BonusClassification.BonusPlus
                    ? BonusClassification.Contract
                    : bonusSettlementSupplier.bonusClassification;

            return bonusSettlementSupplier.bonusConditions?.map((bonusCondition, index) => ({
                id: bonusSettlementSupplier.id * 1000000 + index, //TODO FIX ME: string als id erlauben
                supplier: (
                    <Stack>
                        {bonusSettlementSupplier.supplierName}
                        <PoolTypography typography="body2" component="span" color="grey.800">
                            {bonusSettlementSupplier.supplierId}・{bonusSettlementSupplier.supplierCity}
                        </PoolTypography>
                    </Stack>
                ),
                bonusClassification: (
                    <ValueOrNDash>
                        {bonusClassification && (
                            <FormattedMessage id={getBonusClassificationTranslationId(bonusClassification)} />
                        )}
                    </ValueOrNDash>
                ),
                information: (
                    <BonusSettlementDetailBonusConditionTooltip
                        mode={bonusCondition.mode}
                        title={bonusCondition.title}
                    />
                ),
                modePercentage:
                    bonusCondition.mode === BonusConditionMode.Percentage ? (
                        <FormattedNumber
                            maximumFractionDigits={2}
                            value={bonusCondition.value}
                            style="currency"
                            currency={this.props.currency}
                            key={bonusCondition.title}
                        />
                    ) : (
                        <FormattedNumber
                            maximumFractionDigits={2}
                            value={0}
                            style="currency"
                            currency={this.props.currency}
                            key={bonusCondition.title}
                        />
                    ),
                modeAmount:
                    bonusCondition.mode === BonusConditionMode.Amount ? (
                        <FormattedNumber
                            maximumFractionDigits={2}
                            style="decimal"
                            minimumFractionDigits={2}
                            value={bonusCondition.value}
                            currency={this.props.currency}
                            key={bonusCondition.title}
                        />
                    ) : (
                        <FormattedNumber
                            maximumFractionDigits={2}
                            style="decimal"
                            minimumFractionDigits={2}
                            value={0}
                            currency={this.props.currency}
                            key={bonusCondition.title}
                        />
                    ),
                // The note stood beside the supplier's bonus, which members no
                // longer see (#2285); it goes beside theirs.
                memberBonus: (
                    <Stack flexDirection="row" justifyContent="right" alignItems="center" gap={1}>
                        {bonusSettlementSupplier.noteToCustomer && (
                            <Tooltip title={bonusSettlementSupplier.noteToCustomer} placement="top">
                                <Box sx={{ display: 'flex' }} justifyContent={'center'}>
                                    <CommentNoteIcon
                                        sx={{
                                            color: theme => theme.palette.common.black,
                                            width: 20,
                                            height: 20,
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        <strong>
                            <FormattedNumber
                                value={bonusCondition.memberBonus}
                                style="currency"
                                currency={this.props.currency}
                                maximumFractionDigits={2}
                            />
                        </strong>
                    </Stack>
                ),
            }));
        });
}
